<template>
  <form>
    <b-row>
      <b-col cols="6">
        <b-form-group label="Game Type" label-for="gametype-input">
          <multiselect
              id="gametype-input"
              v-model="selectedGameType"
              :options="gameTypeSelectOptions"
              :searchable="true"
              :internal-search="true"
              label="text"
              track-by="value"
              :disabled="isManage"
              deselect-label=""
              select-label="Select"
              placeholder="Search game type by name or id"
          />
          <small v-if="gameTypeError" class="text-danger">Game Type is required</small>
        </b-form-group>
      </b-col>
    </b-row>

    <hr>

    <b-row class="mb-1 font-weight-bold d-none d-md-flex">
      <b-col md="4">Side Bet</b-col>
      <b-col md="4">Opposite Side Bet</b-col>
      <b-col md="2">Enabled</b-col>
      <b-col md="2"></b-col>
    </b-row>

    <b-row v-for="(row, index) in formValues.rows" :key="row.key" class="mb-2 align-items-center">
      <b-col md="4">
        <b-form-input
            type="number"
            v-model="row.sideBetType"
            v-validate="{ required: true }"
            :state="validateState('sidebet-' + row.key)"
            :data-vv-name="'sidebet-' + row.key"
            data-vv-as="Side Bet"
            placeholder="Side bet code, e.g. 64"
        />
        <b-form-invalid-feedback :state="validateState('sidebet-' + row.key)">
          {{ veeErrors.first('sidebet-' + row.key) }}
        </b-form-invalid-feedback>
      </b-col>
      <b-col md="4">
        <b-form-input
            type="number"
            v-model="row.oppositeSideBetType"
            v-validate="{ required: true }"
            :state="validateState('opposite-' + row.key)"
            :data-vv-name="'opposite-' + row.key"
            data-vv-as="Opposite Side Bet"
            placeholder="Opposite code, e.g. 128"
        />
        <b-form-invalid-feedback :state="validateState('opposite-' + row.key)">
          {{ veeErrors.first('opposite-' + row.key) }}
        </b-form-invalid-feedback>
      </b-col>
      <b-col md="2">
        <b-form-checkbox v-model="row.enabled" switch>{{ row.enabled ? 'On' : 'Off' }}</b-form-checkbox>
      </b-col>
      <b-col md="2">
        <button
            v-if="ready"
            type="button"
            class="btn btn-outline-danger btn-sm"
            @click="removeRow(index)"
        >
          <i class="fas fa-trash"></i>
        </button>
      </b-col>
    </b-row>

    <b-row v-if="ready && !formValues.rows.length" class="mb-2">
      <b-col>
        <span class="text-muted">No opposite side bets. Add one below.</span>
      </b-col>
    </b-row>

    <b-row v-if="ready" class="mt-2">
      <b-col>
        <button type="button" class="btn btn-outline-primary btn-sm" @click="addRow">
          <i class="fas fa-plus"></i> Add opposite side bet
        </button>
      </b-col>
    </b-row>
  </form>
</template>

<script>
import Multiselect from 'vue-multiselect'

export default {
  components: {
    Multiselect
  },
  data() {
    return {
      isManage: false,
      ready: false,
      gameTypeError: false,
      rowSeq: 1,
      formValues: {
        gameType: null,
        rows: [{key: 0, id: null, sideBetType: null, oppositeSideBetType: null, enabled: true}]
      }
    }
  },
  computed: {
    gameTypeSelectOptions() {
      const list = this.$store.getters['gameTypes/list'] || [];
      return list.map(item => ({value: item.id, text: item.id + ' — ' + item.name}));
    },
    selectedGameType: {
      get() {
        return this.gameTypeSelectOptions.find(option => option.value === this.formValues.gameType) || null;
      },
      set(option) {
        this.formValues.gameType = option ? option.value : null;
        if (this.formValues.gameType !== null) {
          this.gameTypeError = false;
        }
      }
    }
  },
  methods: {
    getInitialValues() {
      return {
        gameType: null,
        rows: [this.emptyRow()]
      }
    },
    emptyRow() {
      if (!Number.isFinite(this.rowSeq)) {
        this.rowSeq = 0;
      }
      return {key: this.rowSeq++, id: null, sideBetType: null, oppositeSideBetType: null, enabled: true}
    },
    addRow() {
      this.formValues.rows.push(this.emptyRow());
    },
    removeRow(index) {
      this.formValues.rows.splice(index, 1);
    },
    // data = {} → create mode; data = {gameType, records:[dto...]} → manage mode
    setData(data) {
      this.ready = true;
      this.gameTypeError = false;
      this.rowSeq = 0;
      this.isManage = Array.isArray(data.records);

      if (this.isManage) {
        this.formValues = {
          gameType: data.gameType ?? null,
          rows: data.records.map(record => ({
            key: this.rowSeq++,
            id: record.id,
            sideBetType: record.sideBetType,
            oppositeSideBetType: Array.isArray(record.oppositeSideBetTypes) ? (record.oppositeSideBetTypes[0] ?? null) : null,
            enabled: record.enabled ?? true
          }))
        };
      } else {
        this.formValues = this.getInitialValues();
      }

      this.$nextTick(() => {
        if (this.$validator) {
          this.$validator.reset();
        }
      });
    },
    getData() {
      return {
        isManage: this.isManage,
        gameType: Number(this.formValues.gameType),
        items: this.formValues.rows.map(row => {
          const item = {
            sideBetType: Number(row.sideBetType),
            oppositeSideBetType: Number(row.oppositeSideBetType),
            enabled: !!row.enabled
          };
          if (row.id) {
            item.id = row.id;
          }
          return item;
        })
      };
    },
    isValid() {
      return this.$validator.validateAll().then(result => {
        this.gameTypeError = this.formValues.gameType === null || this.formValues.gameType === undefined;
        if (this.gameTypeError) {
          this.$toastr.e('Game Type is required');
          return false;
        }

        if (!this.isManage && this.formValues.rows.length === 0) {
          this.$toastr.e('Add at least one opposite side bet');
          return false;
        }

        if (!result) {
          return false;
        }

        // guard: side bet must differ from its opposite, and no duplicate side bets
        const seen = {};
        for (const row of this.formValues.rows) {
          if (Number(row.sideBetType) === Number(row.oppositeSideBetType)) {
            this.$toastr.e('Side Bet and Opposite Side Bet must be different');
            return false;
          }
          if (seen[row.sideBetType]) {
            this.$toastr.e('Side Bet ' + row.sideBetType + ' is defined more than once');
            return false;
          }
          seen[row.sideBetType] = true;
        }
        return true;
      });
    },
    validateState(ref) {
      if (
          this.veeFields[ref] &&
          (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref);
      }
      return null;
    }
  },
  mounted() {
    this.$store.dispatch('gameTypes/loadList');
  }
}
</script>
